/**
 * x-turnstile directive.
 *
 * Renders a Cloudflare Turnstile widget on the bound element and dispatches
 * `turnstile-verified`, `turnstile-expired`, and `turnstile-error` events
 * (all bubbling) so a parent component can gate submission on a verified
 * human check. The widget itself injects a hidden `cf-turnstile-response`
 * input into the element, which rides along automatically with any
 * `FormData` read from an ancestor `<form>` - no manual wiring needed to
 * include the token in a submission payload.
 *
 * @options
 *   sitekey - The Turnstile site key (public, safe to expose in markup).
 *   theme - 'auto' | 'light' | 'dark' (default: 'auto').
 *   size - 'normal' | 'flexible' | 'compact' (default: 'flexible').
 *   appearance - 'always' | 'execute' | 'interaction-only' (default: 'interaction-only').
 *
 * @example
 *   <div x-turnstile="{ sitekey: '0x000000000000000000AA' }"></div>
 */
(() => {
  const SCRIPT_URL =
    "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

  /** @type {Promise<object>|null} Resolves to the global `turnstile` API once loaded. */
  let scriptPromise = null;

  /**
   * Loads the Turnstile script once and caches the in-flight promise so
   * multiple widgets on a page share a single script load.
   * @returns {Promise<object>} Resolves to the global `turnstile` API.
   */
  const loadScript = () => {
    if (window.turnstile) {
      return Promise.resolve(window.turnstile);
    }

    if (!scriptPromise) {
      scriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = SCRIPT_URL;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.turnstile);
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    return scriptPromise;
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive(
      "turnstile",
      (el, { expression }, { evaluate, cleanup }) => {
        const {
          sitekey,
          theme = "auto",
          size = "flexible",
          appearance = "interaction-only",
        } = expression ? evaluate(expression) : {};

        if (!sitekey) {
          return;
        }

        let widgetId = null;

        loadScript().then((turnstile) => {
          widgetId = turnstile.render(el, {
            sitekey,
            theme,
            size,
            appearance,
            "response-field": true,
            "response-field-name": "cf-turnstile-response",
            callback: (token) => {
              el.dispatchEvent(
                new CustomEvent("turnstile-verified", {
                  detail: { token },
                  bubbles: true,
                }),
              );
            },
            "expired-callback": () => {
              el.dispatchEvent(
                new CustomEvent("turnstile-expired", { bubbles: true }),
              );
            },
            "error-callback": () => {
              el.dispatchEvent(
                new CustomEvent("turnstile-error", { bubbles: true }),
              );
            },
          });
        });

        cleanup(() => {
          if (null !== widgetId && window.turnstile) {
            window.turnstile.remove(widgetId);
          }
        });
      },
    );
  });
})();
